/*
  DA CUSTOM. Announcement bar (sections/da-announcement.liquid): shows the
  messages one at a time with previous, next and pause buttons and swipe, and
  runs the offer countdown. The offer shows its end date until its last hours
  (data-window on .da-bar__when), then the live clock. At the end time the
  offer hides and the messages rotate again. No rotation for visitors who
  turned off motion.
*/
(() => {
  if (customElements.get('da-announcement')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Time in milliseconds since 1970, from Temporal where the browser has it
  const hasTemporal = typeof Temporal !== 'undefined';
  const nowMs = () => (hasTemporal ? Temporal.Now.instant().epochMilliseconds : Date.now());
  const toMs = (iso) => {
    if (hasTemporal) {
      try { return Temporal.Instant.from(iso).epochMilliseconds; } catch (e) { return NaN; }
    }
    return Date.parse(iso);
  };
  const two = (n) => String(n).padStart(2, '0');

  class DaAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.da-bar__slide'));
      this.track = this.querySelector('.da-bar__slides');
      this.toggle = this.querySelector('[data-da-toggle]');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.speed = Math.max(2, parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.autoplay = this.hasAttribute('data-autoplay') && this.slides.length > 1;
      this.stopped = false;
      this.hovered = false;
      this.focused = false;
      this.editing = false;
      this.offer = this.querySelector('.da-bar__offer:not([hidden])');

      this.events = new AbortController();
      const on = (target, type, handler) => target && target.addEventListener(type, handler, { signal: this.events.signal });

      on(this.querySelector('[data-da-prev]'), 'click', () => this.go(this.index - 1));
      on(this.querySelector('[data-da-next]'), 'click', () => this.go(this.index + 1));
      on(this.toggle, 'click', () => { this.stopped = !this.stopped; this.update(); });

      // pause while the mouse is over a message or the keyboard is inside the bar
      on(this.track, 'pointerenter', (event) => { if (event.pointerType === 'mouse') { this.hovered = true; this.update(); } });
      on(this.track, 'pointerleave', (event) => { if (event.pointerType === 'mouse') { this.hovered = false; this.update(); } });
      on(this, 'focusin', (event) => { this.focused = !event.target.closest('[data-da-toggle]'); this.update(); });
      on(this, 'focusout', (event) => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.update(); } });

      // swipe on touch screens; the page still scrolls up and down
      on(this.track, 'pointerdown', (event) => { this.swipe = event.pointerType === 'mouse' ? null : [event.clientX, event.clientY]; });
      on(this.track, 'pointercancel', () => { this.swipe = null; });
      on(this.track, 'pointerup', (event) => {
        if (!this.swipe) return;
        const dx = event.clientX - this.swipe[0];
        const dy = event.clientY - this.swipe[1];
        this.swipe = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) this.go(this.index + (dx < 0 ? 1 : -1));
      });

      on(document, 'visibilitychange', () => { this.tick(); this.update(); });
      on(window, 'pageshow', () => this.tick());
      on(reduceMotion, 'change', () => this.update());

      // theme editor: show the selected message
      on(this, 'shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target.closest('.da-bar__slide'));
        if (index < 0) return;
        this.editing = true;
        if (this.dataset.state === 'offer') { this.dataset.state = 'messages'; this.pinnedBeforeEdit = true; }
        this.go(index);
      });
      on(this, 'shopify:block:deselect', () => {
        this.editing = false;
        if (this.pinnedBeforeEdit) { this.pinnedBeforeEdit = false; this.dataset.state = 'offer'; this.tick(); }
        this.update();
      });

      if (this.offer) {
        this.end = toMs(this.offer.dataset.end);
        this.numbers = Array.from(this.offer.querySelectorAll('[data-da-num]'));
        this.when = this.offer.querySelector('.da-bar__when');
        this.tick();
      }

      // the real bar height for the theme's sticky product media; written only when
      // it changes, a new value on the root restyles the whole page
      if (window.ResizeObserver) {
        this.resize = new ResizeObserver((entries) => {
          const height = Math.round(entries[0].borderBoxSize ? entries[0].borderBoxSize[0].blockSize : this.offsetHeight) + 'px';
          if (height === this.topbarHeight) return;
          this.topbarHeight = height;
          document.documentElement.style.setProperty('--topbar-height', height);
        });
        this.resize.observe(this);
      }

      this.update();
    }

    disconnectedCallback() {
      this.events.abort();
      if (this.resize) this.resize.disconnect();
      clearTimeout(this.rotateTimer);
      clearTimeout(this.tickTimer);
      this.rotateTimer = null;
    }

    go(index) {
      const count = this.slides.length;
      if (count < 2) return;
      const next = (index + count) % count;
      if (next !== this.index) {
        this.slides[this.index].classList.remove('is-active');
        this.slides[next].classList.add('is-active');
        this.index = next;
      }
      clearTimeout(this.rotateTimer);
      this.rotateTimer = null;
      this.update();
    }

    update() {
      const running = this.autoplay && this.dataset.state !== 'offer' && !this.stopped && !this.hovered
        && !this.focused && !this.editing && !reduceMotion.matches && !document.hidden;

      if (this.toggle) {
        this.toggle.classList.toggle('is-stopped', this.stopped);
        this.toggle.setAttribute('aria-label', this.stopped ? this.toggle.dataset.play : this.toggle.dataset.pause);
      }
      // a screen reader hears a new message only when someone changed it on purpose
      if (this.track && this.slides.length > 1) this.track.setAttribute('aria-live', running ? 'off' : 'polite');

      if (running && !this.rotateTimer) {
        this.rotateTimer = setTimeout(() => { this.rotateTimer = null; this.go(this.index + 1); }, this.speed);
      } else if (!running && this.rotateTimer) {
        clearTimeout(this.rotateTimer);
        this.rotateTimer = null;
      }
    }

    tick() {
      clearTimeout(this.tickTimer);
      if (!this.offer || this.offer.hidden || this.dataset.state !== 'offer') return;
      const left = this.end - nowMs();
      if (!(left > 0)) { this.endOffer(); return; }

      // the end date until the last hours, then the clock; checked once a minute until then
      if (this.when) {
        const windowMs = (parseFloat(this.when.dataset.window) || 0) * 3600000;
        const mode = windowMs && left > windowMs ? 'date' : 'clock';
        if (this.when.dataset.mode !== mode) this.when.dataset.mode = mode;
        if (mode === 'date') {
          this.tickTimer = setTimeout(() => this.tick(), Math.min(left - windowMs + 50, 60000));
          return;
        }
      }

      const total = Math.ceil(left / 1000);
      const values = [Math.floor(total / 86400), Math.floor(total / 3600) % 24, Math.floor(total / 60) % 60, total % 60];
      this.numbers.forEach((el, i) => {
        const text = two(values[i]);
        if (el.textContent !== text) el.textContent = text;
      });
      // no "00 days" in the last day
      const dayUnit = this.numbers[0] && this.numbers[0].closest('.da-bar__unit');
      if (dayUnit && dayUnit.hidden !== (values[0] === 0)) dayUnit.hidden = values[0] === 0;
      // wake up when the shown second changes
      this.tickTimer = setTimeout(() => this.tick(), (left % 1000 || 1000) + 15);
    }

    // the offer keeps its space until the next page, so nothing below moves
    endOffer() {
      this.dataset.state = 'messages';
      this.pinnedBeforeEdit = false;
      if (!this.slides.length) this.closest('.da-bar').hidden = true;
      this.update();
    }
  }

  customElements.define('da-announcement', DaAnnouncement);
})();
